import { useEffect, useState } from 'react';
import { pagoService, prestamoService } from '../../services/api';
import '../../styles/Reportes.css';

const VariacionesCapital = () => {
  const [fechaInicio, setFechaInicio] = useState('');
  const [fechaFin, setFechaFin] = useState('');
  const [datos, setDatos] = useState(null);
  const [cargando, setCargando] = useState(false);

  useEffect(() => {
    // Establecer fechas por defecto (último mes)
    const hoy = new Date();
    const haceUnMes = new Date();
    haceUnMes.setMonth(haceUnMes.getMonth() - 1);

    setFechaInicio(haceUnMes.toISOString().split('T')[0]);
    setFechaFin(hoy.toISOString().split('T')[0]);
  }, []);

  const cargarDatos = async () => {
    if (!fechaInicio || !fechaFin) {
      alert('Por favor selecciona ambas fechas');
      return;
    }

    setCargando(true);
    try {
      const [responsePagos, responsePrestamos] = await Promise.all([
        pagoService.getAll(),
        prestamoService.getAll()
      ]);

      const todosPagos = responsePagos.data.pagos || [];
      const todosPrestamos = responsePrestamos.data.prestamos || [];

      const inicio = new Date(fechaInicio);
      const fin = new Date(fechaFin);
      fin.setHours(23, 59, 59);

      // Capital inicial (préstamos antes del período)
      const prestamosAnteriores = todosPrestamos.filter(p => new Date(p.fecha_inicio) < inicio);
      const capitalInicial = prestamosAnteriores.reduce((sum, p) => sum + p.monto_prestado, 0);

      // Movimientos en el período
      const prestamosEnPeriodo = todosPrestamos.filter(p => {
        const fecha = new Date(p.fecha_inicio);
        return fecha >= inicio && fecha <= fin;
      });

      const pagosEnPeriodo = todosPagos.filter(p => {
        const fecha = new Date(p.fecha_pago);
        return fecha >= inicio && fecha <= fin;
      });

      // Calcular egresos (préstamos otorgados)
      const totalEgresos = prestamosEnPeriodo.reduce((sum, p) => sum + p.monto_prestado, 0);

      // Calcular ingresos (capital recuperado)
      const totalIngresos = pagosEnPeriodo.reduce((sum, p) => sum + (p.monto_capital || 0), 0);

      // Capital final
      const capitalFinal = capitalInicial + totalIngresos - totalEgresos;

      // Variación neta
      const variacionNeta = totalIngresos - totalEgresos;

      // Crear timeline diario
      const timeline = [];
      let capitalActual = capitalInicial;
      const dias = Math.ceil((fin - inicio) / (1000 * 60 * 60 * 24));

      for (let i = 0; i <= dias; i++) {
        const fecha = new Date(inicio);
        fecha.setDate(fecha.getDate() + i);
        const fechaStr = fecha.toISOString().split('T')[0];

        // Egresos del día
        const egresosDelDia = prestamosEnPeriodo
          .filter(p => p.fecha_inicio.split('T')[0] === fechaStr)
          .reduce((sum, p) => sum + p.monto_prestado, 0);

        // Ingresos del día
        const ingresosDelDia = pagosEnPeriodo
          .filter(p => p.fecha_pago.split('T')[0] === fechaStr)
          .reduce((sum, p) => sum + (p.monto_capital || 0), 0);

        const movimiento = ingresosDelDia - egresosDelDia;
        capitalActual += movimiento;

        timeline.push({
          fecha: fechaStr,
          ingresos: ingresosDelDia,
          egresos: egresosDelDia,
          movimiento,
          capitalAcumulado: capitalActual
        });
      }

      // Agrupar por semana para visualización más limpia
      const semanas = [];
      let semanaActual = { ingresos: 0, egresos: 0, inicio: null, fin: null };

      timeline.forEach((dia, index) => {
        if (index % 7 === 0 && index > 0) {
          semanas.push(semanaActual);
          semanaActual = { ingresos: 0, egresos: 0, inicio: dia.fecha, fin: dia.fecha };
        }
        if (!semanaActual.inicio) semanaActual.inicio = dia.fecha;
        semanaActual.fin = dia.fecha;
        semanaActual.ingresos += dia.ingresos;
        semanaActual.egresos += dia.egresos;
      });
      if (semanaActual.inicio) semanas.push(semanaActual);

      setDatos({
        capitalInicial,
        capitalFinal,
        totalIngresos,
        totalEgresos,
        variacionNeta,
        timeline: timeline.filter((_, i) => i % 3 === 0 || i === timeline.length - 1), // Mostrar cada 3 días
        semanas,
        cantidadPrestamos: prestamosEnPeriodo.length,
        cantidadPagos: pagosEnPeriodo.length
      });
    } catch (error) {
      alert('Error al calcular variaciones');
      console.error(error);
    } finally {
      setCargando(false);
    }
  };

  const formatearFecha = (fecha) => {
    return new Date(fecha).toLocaleDateString('es-DO', {
      day: '2-digit',
      month: '2-digit'
    });
  };

  return (
    <div className="reporte-detalle">
      <div className="reporte-header">
        <h1>📊 Variaciones de Capital</h1>
        <p>Seguimiento de movimientos de capital en el tiempo</p>
      </div>

      <div className="filtros-container">
        <div className="filtro-grupo">
          <label>Fecha Inicial:</label>
          <input
            type="date"
            value={fechaInicio}
            onChange={(e) => setFechaInicio(e.target.value)}
            className="input-text"
          />
        </div>

        <div className="filtro-grupo">
          <label>Fecha Final:</label>
          <input
            type="date"
            value={fechaFin}
            onChange={(e) => setFechaFin(e.target.value)}
            className="input-text"
          />
        </div>

        <button onClick={cargarDatos} className="btn-buscar">
          Generar Análisis
        </button>
      </div>

      {cargando && (
        <div className="loading">Calculando variaciones de capital...</div>
      )}

      {!cargando && datos && (
        <>
          <div className="resumen-cards">
            <div className="card-resumen" style={{ borderLeft: '4px solid #3498db' }}>
              <div className="card-icono">💰</div>
              <div className="card-info">
                <div className="card-valor">${datos.capitalInicial.toFixed(2)}</div>
                <div className="card-label">Capital Inicial</div>
              </div>
            </div>

            <div className="card-resumen" style={{ borderLeft: '4px solid #27ae60' }}>
              <div className="card-icono">📈</div>
              <div className="card-info">
                <div className="card-valor" style={{ color: '#27ae60' }}>
                  ${datos.totalIngresos.toFixed(2)}
                </div>
                <div className="card-label">Capital Recuperado</div>
                <small>{datos.cantidadPagos} pagos</small>
              </div>
            </div>

            <div className="card-resumen" style={{ borderLeft: '4px solid #e74c3c' }}>
              <div className="card-icono">📉</div>
              <div className="card-info">
                <div className="card-valor" style={{ color: '#e74c3c' }}>
                  ${datos.totalEgresos.toFixed(2)}
                </div>
                <div className="card-label">Capital Prestado</div>
                <small>{datos.cantidadPrestamos} préstamos</small>
              </div>
            </div>

            <div className="card-resumen" style={{ borderLeft: `4px solid ${datos.variacionNeta >= 0 ? '#27ae60' : '#e74c3c'}` }}>
              <div className="card-icono">{datos.variacionNeta >= 0 ? '✅' : '⚠️'}</div>
              <div className="card-info">
                <div className="card-valor" style={{
                  color: datos.variacionNeta >= 0 ? '#27ae60' : '#e74c3c'
                }}>
                  {datos.variacionNeta >= 0 ? '+' : ''}${datos.variacionNeta.toFixed(2)}
                </div>
                <div className="card-label">Variación Neta</div>
              </div>
            </div>
          </div>

          <div className="info-prestamo" style={{ marginTop: '30px' }}>
            <h3>📈 Capital Final del Período</h3>
            <div className="info-grid">
              <div>
                <strong>Capital Final:</strong>
                <span style={{ fontSize: '1.5em', marginLeft: '10px', color: '#2c3e50' }}>
                  ${datos.capitalFinal.toFixed(2)}
                </span>
              </div>
              <div>
                <strong>Cambio:</strong>
                <span style={{
                  fontSize: '1.2em',
                  marginLeft: '10px',
                  color: datos.variacionNeta >= 0 ? '#27ae60' : '#e74c3c'
                }}>
                  {datos.variacionNeta >= 0 ? '+' : ''}{((datos.variacionNeta / datos.capitalInicial) * 100).toFixed(1)}%
                </span>
              </div>
            </div>
          </div>

          <div className="info-prestamo" style={{ marginTop: '30px' }}>
            <h3>📅 Movimientos Semanales</h3>
            <div className="tabla-container">
              <table className="tabla-reporte">
                <thead>
                  <tr>
                    <th>Semana</th>
                    <th>Ingresos</th>
                    <th>Egresos</th>
                    <th>Balance</th>
                  </tr>
                </thead>
                <tbody>
                  {datos.semanas.map((semana, index) => {
                    const balance = semana.ingresos - semana.egresos;
                    return (
                      <tr key={index}>
                        <td>
                          {formatearFecha(semana.inicio)} - {formatearFecha(semana.fin)}
                        </td>
                        <td style={{ color: '#27ae60' }}>${semana.ingresos.toFixed(2)}</td>
                        <td style={{ color: '#e74c3c' }}>${semana.egresos.toFixed(2)}</td>
                        <td style={{
                          color: balance >= 0 ? '#27ae60' : '#e74c3c',
                          fontWeight: 'bold'
                        }}>
                          {balance >= 0 ? '+' : ''}${balance.toFixed(2)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          <div className="info-prestamo" style={{ marginTop: '30px' }}>
            <h3>📊 Timeline de Capital (cada 3 días)</h3>
            <div className="tabla-container">
              <table className="tabla-reporte">
                <thead>
                  <tr>
                    <th>Fecha</th>
                    <th>Ingresos</th>
                    <th>Egresos</th>
                    <th>Movimiento</th>
                    <th>Capital Acumulado</th>
                  </tr>
                </thead>
                <tbody>
                  {datos.timeline.map((dia, index) => (
                    <tr key={index}>
                      <td>{formatearFecha(dia.fecha)}</td>
                      <td style={{ color: dia.ingresos > 0 ? '#27ae60' : '#999' }}>
                        ${dia.ingresos.toFixed(2)}
                      </td>
                      <td style={{ color: dia.egresos > 0 ? '#e74c3c' : '#999' }}>
                        ${dia.egresos.toFixed(2)}
                      </td>
                      <td style={{
                        color: dia.movimiento >= 0 ? '#27ae60' : '#e74c3c',
                        fontWeight: dia.movimiento !== 0 ? 'bold' : 'normal'
                      }}>
                        {dia.movimiento >= 0 ? '+' : ''}${dia.movimiento.toFixed(2)}
                      </td>
                      <td><strong>${dia.capitalAcumulado.toFixed(2)}</strong></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {!cargando && !datos && (
        <div className="sin-datos">
          <p>Selecciona un período y genera el análisis de variaciones</p>
        </div>
      )}
    </div>
  );
};

export default VariacionesCapital;
